/**
 * Which routes need the page's pre-content paint to be black instead of
 * `--color-background`.
 *
 * ## The window this colours
 *
 * The browser paints `<body>` on `--color-background` (#f6f6f6) before the
 * page's own hero has painted. On a light page that is invisible; on a dark
 * full-bleed hero it reads as a white flash, so this stamps
 * `html[data-shell="dark"]` and `globals.css` paints that window black instead.
 *
 * That window used to be ~400 ms wide, because nothing a visitor could see was
 * in the static shell at all: both route-group layouts
 * (`(transparent-nav)/layout.tsx`, `(default-nav)/layout.tsx`) did
 * `await getServicesConfig()` above `{children}`, below the locale layout's
 * `<Suspense fallback={null}>`, so React flushed a `<!--$?-->` placeholder and
 * the navbar, `<main>` and footer all arrived later in a `<div hidden>` staging
 * container. Measured on prod 2026-07-28 (1440×900, warm edge cache):
 * first-paint at 456 ms with the top band at luminance 247, content only at
 * 870 ms at luminance 8.
 *
 * AHD-9096 closed that: the CMS read moved up into `[locale]/layout.tsx`, where
 * it has no Suspense ancestor, and both route-group layouts are synchronous —
 * so the chrome and the page are back in the prerendered HTML, in document
 * order. What's left for this attribute to cover is the genuinely
 * unavoidable part: the paint that happens before the hero's own styles and
 * image land, plus the overscroll gutter.
 *
 * A pre-paint `<head>` script stamps `html[data-shell="dark"]` from
 * `location.pathname` on first paint (and `<DarkShellSync />` keeps it in step
 * on every soft navigation after that), and `globals.css` paints that black —
 * the hero's own
 * `bg-neutral-950`, so the reveal is black-to-black instead of white-to-black.
 * Same shape as the `html[data-mobile-menu-open]` attribute already in that
 * file.
 *
 * Safe to leave set for as long as the visitor is ON one of these routes: zero
 * pixels of `<body>` background are visible there once content has painted
 * (measured by painting it magenta and scanning the full-page screenshot), so
 * the only thing this changes is the blank window and the overscroll gutter —
 * and under a black hero, a black gutter is the right answer anyway.
 *
 * NOT safe to leave set for the whole page LIFE, which is the bug PR #2156
 * shipped. A soft navigation outlives the route: `<head>` is never re-parsed,
 * so clicking from `/` to `/faq` carried `data-shell="dark"` onto a light page,
 * where the `(default-nav)` `<header>` — `sticky top-0`, no background of its
 * own, `bg-white/70 backdrop-blur-md` pill — has the body as its literal
 * backdrop and rendered as a black band with a grey pill. Reported 2026-08-04
 * and again 08-06 on `/ch/de/faq`, `/ch/de/membership`, `/ch/de/locations`; it
 * read as "1 in 20" and nobody could reproduce it from a cold URL, because a
 * hard load re-runs the script on the light path and stamps nothing.
 * `components/layout/dark-shell-sync.tsx` re-evaluates the attribute on every
 * route change, so this file's job is now first paint only.
 *
 * ## Scope
 *
 * Exactly the `(transparent-nav)` route group, which exists *because* those
 * routes overlay the navbar on a full-bleed hero. Measured above-fold
 * luminance on prod: home 100, about 77, reviews 46,
 * go/immunity 78, go/metabolism 58 — against 217 (pricing) and 222 (imprint)
 * for default-nav pages. `__tests__/dark-shell-routes.test.ts` asserts this
 * list stays equal to the route group's actual contents, so a new hero page
 * can't be added without either listing it or deliberately excluding it.
 *
 * ## Still worth keeping after AHD-9096
 *
 * The structural fix removed the blank window this was introduced for, so this
 * is no longer load-bearing against a ~400 ms hold. It stays because the
 * remaining pre-hero paint and the overscroll gutter are still `<body>`
 * background, and under a black hero a black one is the right answer — and
 * because it is the cheap insurance if a future await ever creeps back into a
 * route-group layout. `e2e/static-shell.spec.ts` is the thing that would
 * actually catch that; this only softens it.
 */

import { localizedVariants } from "@/lib/localized-route-slugs"

/**
 * Public sub-paths (everything after `/{country}/{locale}`) of the
 * `(transparent-nav)` route group, under the route folder's own name.
 * `""` is the homepage.
 */
export const DARK_SHELL_CANONICAL_SUBPATHS = [
  "", // (transparent-nav)/v/[code] — the homepage
  "about",
  "reviews",
  "go/immunity",
  "go/metabolism",
  "go/female-hormones",
  "go/lauraphilipps",
  "go/heart-health",
  "go/checkup", // (transparent-nav)/go/checkup/v/[code]
] as const

/**
 * The list the head script and {@link isDarkShellPath} match against: the
 * route folders above plus the translated URL each one is also served under
 * (`lib/localized-route-slugs.ts`). `/ch/de/ueber-uns` paints the same dark hero
 * as `/ch/en/about` — without this the German and French pages of a dark-hero
 * route flash white before first paint.
 */
export const DARK_SHELL_SUBPATHS: readonly string[] = [
  ...DARK_SHELL_CANONICAL_SUBPATHS,
  ...DARK_SHELL_CANONICAL_SUBPATHS.flatMap(localizedVariants),
]

/**
 * Does this browser pathname resolve to a dark-hero route?
 *
 * Mirrors the logic inlined into {@link DARK_SHELL_HEAD_SCRIPT} so the
 * behaviour is unit-testable. Tolerates the `v/{code}` precompute pair even
 * though `proxy.ts`'s rewrite is invisible to the address bar (see
 * `lib/public-pathname.ts`), so hitting an internal URL directly still gets
 * the right backdrop.
 */
export function isDarkShellPath(pathname: string): boolean {
  const segments = pathname.split("/").filter(Boolean)
  // Below `/{country}/{locale}` there is no page to colour — a bare `/` only
  // ever 308s to a localised path, so it never paints.
  if (segments.length < 2) return false
  const rest = segments.slice(2)
  const v = rest.indexOf("v")
  if (v > -1 && v < rest.length - 1) rest.splice(v, 2)
  return (DARK_SHELL_SUBPATHS as readonly string[]).includes(rest.join("/"))
}

/**
 * Pre-paint `<head>` script. Must run synchronously during head parse — before
 * the browser's first paint — so it is injected as a raw inline `<script>`,
 * not via `next/script` (whose `beforeInteractive` still hands scheduling to
 * Next's loader). Wrapped in try/catch: a throw here would abort head parsing
 * and cost far more than the flash it prevents.
 */
export const DARK_SHELL_HEAD_SCRIPT = `(function(){try{var p=${JSON.stringify(
  DARK_SHELL_SUBPATHS
)};var s=location.pathname.split("/").filter(Boolean);if(s.length<2)return;var r=s.slice(2);var v=r.indexOf("v");if(v>-1&&v<r.length-1)r.splice(v,2);if(p.indexOf(r.join("/"))>-1)document.documentElement.setAttribute("data-shell","dark")}catch(e){}})();`
